import {I18n} from 'common/translator/i18n';

import {
  createLoginButton,
  LoginWithShopButton,
} from '../components/login-with-shop-button';
import {
  ATTRIBUTE_ANALYTICS_TRACE_ID,
  ATTRIBUTE_ANCHOR_SELECTOR,
} from '../../../constants/loginButton';
import {
  DefaultComponentAnalyticsContext,
  FOOTER_CLASS_NAME,
  FOOTER_CONTENT_CLASS_NAME,
  HEADER_CLASS_NAME,
  HEADER_CONTENTS_CONTAINER_CLASS_NAME,
  HEADER_DESCRIPTION_CLASS_NAME,
  HEADER_DIVIDER_CLASS_NAME,
  HEADER_STYLE,
  HEADER_TITLE_CLASS_NAME,
  HIDDEN_ELEMENT_CLASS_NAME,
  IFRAME_CLASS_NAME,
  SHOP_LOGIN_DEFAULT_HTML,
  STORE_NAME_TEMPLATE,
} from '../../../constants/loginDefault';
import {ShopSheetModal} from '../../../common/shop-sheet-modal/shop-sheet-modal';
import {LoginWithShopSdkPageName} from '../../../common/analytics';
import {copyTemplateToDom, updateAttribute} from '../../../common/utils';
import MonorailTracker from '../analytics/DefaultComponentMonorailTracker';
import {
  AuthenticationLevel,
  ModalCustomizations,
  AuthorizeModalTextViewState,
  AuthorizeModalTextAction,
} from '../../../types';
import {sanitizeModalText as sanitizeModalCustomization} from '../utils/modalCustomizations';
import {
  classicCustomerAccountsSignUpReducer,
  DEFAULT_STATE,
  defaultEmailAuthReducer,
  defaultPhoneAuthReducer,
} from '../utils/authorizeModalTextReducer';

export default class LoginDefaultView {
  private rootElement: ShadowRoot;
  private sheetModal!: ShopSheetModal;
  private modalHeader: HTMLDivElement;
  private headerIframe: HTMLIFrameElement | undefined;
  private headerContentsContainer: HTMLDivElement | undefined;
  private headerLogo: HTMLImageElement | undefined;
  private headerTitle: HTMLElement | undefined;
  private headerDescription: HTMLElement | undefined;
  private footerElement: HTMLDivElement;
  private footerContent: HTMLDivElement;

  private iframe: HTMLIFrameElement;
  private headerResizeObserver: ResizeObserver | undefined;

  private _modalTitle: string | undefined;
  private _modalDescription: string | undefined;
  private _modalLogoSrc: string | undefined;

  private _continueButton: LoginWithShopButton | undefined;

  private monorailTracker: MonorailTracker | undefined;
  private analyticsContext = DefaultComponentAnalyticsContext.Default;
  private flowVersion = 'unspecified';
  private authenticationLevel: AuthenticationLevel = AuthenticationLevel.Phone;

  private i18n: I18n | undefined;
  private state: AuthorizeModalTextViewState = DEFAULT_STATE;

  constructor(rootElement: ShadowRoot, onClose: () => void) {
    this.rootElement = rootElement;

    copyTemplateToDom(
      SHOP_LOGIN_DEFAULT_HTML,
      'shop-login-default-landing',
      this.rootElement,
    );

    this.sheetModal = this.rootElement.querySelector('shop-sheet-modal')!;
    this.modalHeader = this.rootElement.querySelector(`.${HEADER_CLASS_NAME}`)!;
    this.footerElement = this.rootElement.querySelector(
      `.${FOOTER_CLASS_NAME}`,
    )!;
    this.footerContent = this.rootElement.querySelector(
      `.${FOOTER_CONTENT_CLASS_NAME}`,
    )!;
    this.iframe = this.rootElement.querySelector(`.${IFRAME_CLASS_NAME}`)!;

    this.sheetModal.addEventListener('modalcloserequest', () => {
      this.closeAuthorizeModal();
      onClose();
    });

    updateAttribute(this.iframe, 'allow', 'publickey-credentials-get *');
  }

  setModalAnalyticsTraceId(analyticsTraceId: string): void {
    this.sheetModal.setAttribute(
      ATTRIBUTE_ANALYTICS_TRACE_ID,
      analyticsTraceId,
    );
  }

  async showModal() {
    await this.sheetModal.open();
    this.refreshHeaderIframeHeight();
  }

  async openAuthorizeModal() {
    this.showModal();
    this.monorailTracker?.trackPageImpression({
      page: LoginWithShopSdkPageName.AuthorizeModal,
    });
  }

  async closeAuthorizeModal() {
    if (this.sheetModal) {
      await this.sheetModal.close();
    }
    this._continueButton?.setFocused();
  }

  setContinueButtonVisible(isVisible: boolean) {
    if (!isVisible) {
      this._continueButton?.remove();
      this._continueButton = undefined;
      return;
    }

    if (!this._continueButton) {
      this._continueButton = createLoginButton();
      this._continueButton?.addEventListener('click', () => {
        this.monorailTracker?.trackLoginDefaultButtonClicked();
        this.openAuthorizeModal();
      });

      this.rootElement.appendChild(this._continueButton);
    }
  }

  setMonorailTracker(monorailTracker: MonorailTracker) {
    this.monorailTracker = monorailTracker;
  }

  setAnalyticsContext(analyticsContext: DefaultComponentAnalyticsContext) {
    this.analyticsContext = analyticsContext;
  }

  setFlowVersion(flowVersion: string) {
    this.flowVersion = flowVersion;
  }

  setAuthenticationLevel(authenticationLevel: AuthenticationLevel) {
    this.authenticationLevel = authenticationLevel;
  }

  getIframe(): HTMLIFrameElement {
    return this.iframe;
  }

  setAnchorSelector(anchorSelector: string) {
    this.sheetModal.setAttribute(ATTRIBUTE_ANCHOR_SELECTOR, anchorSelector);
  }

  resizeIframe(height: number, width: number) {
    if (!this.iframe) return;

    this.iframe.style.height = `${height}px`;
    this.iframe.style.width = `${width}px`;
  }

  render() {
    const {
      configurable,
      headerVisible,
      headerDividerVisible,
      legalTextVariant,
    } = this.state;

    this.setHeaderVisible(headerVisible);
    this.setHeaderDividerVisible(headerDividerVisible);
    this.setFooterVisible(Boolean(legalTextVariant));

    if (configurable && this._modalLogoSrc) {
      this.updateHeaderLogo(this._modalLogoSrc);
    }

    this.renderHeaderTemplate();
    this.renderHeaderDescriptionTemplate();
    this.renderFooterContent();

    this.refreshHeaderIframeHeight();
  }

  dispatch(action: AuthorizeModalTextAction) {
    switch (this.analyticsContext) {
      case DefaultComponentAnalyticsContext.CheckoutExtension:
      case DefaultComponentAnalyticsContext.Default:
        this.state =
          this.authenticationLevel === AuthenticationLevel.Phone
            ? defaultPhoneAuthReducer(action, this.state)
            : defaultEmailAuthReducer(action, this.state);
        break;
      case DefaultComponentAnalyticsContext.ClassicCustomerAccounts:
        this.state =
          this.flowVersion === 'sign_up'
            ? classicCustomerAccountsSignUpReducer(action, this.state)
            : defaultPhoneAuthReducer(action, this.state);
        break;
      default:
        this.state = DEFAULT_STATE;
        break;
    }

    this.render();
  }

  updateHeaderLogo(src: string) {
    if (!this.headerLogo) {
      this.headerLogo = document.createElement('img');
    }

    const shopSwirl = this.headerContentsContainer?.querySelector('shop-swirl');
    if (shopSwirl) {
      this.headerContentsContainer?.insertBefore(this.headerLogo, shopSwirl);
      shopSwirl?.remove();
    }

    this.headerLogo.src = src;
    this.headerLogo.style.maxHeight = '64px';
    this.headerLogo.style.width = 'auto';
  }

  renderHeaderTemplate() {
    const {configurable, headerTemplate, templateVariables} = this.state;
    let headerTitle = '';

    if (configurable && this._modalTitle) {
      headerTitle = this._modalTitle.replaceAll(
        STORE_NAME_TEMPLATE,
        templateVariables.clientName,
      );
    } else if (headerTemplate) {
      headerTitle =
        this.i18n?.translate(headerTemplate, templateVariables) || '';
    }

    if (typeof headerTitle === 'string') {
      this.headerTitle!.textContent = headerTitle;
    }
  }

  renderHeaderDescriptionTemplate() {
    const {configurable, descriptionTemplate, templateVariables} = this.state;
    let headerDescription = '';

    if (configurable && this._modalDescription) {
      headerDescription = this._modalDescription?.replaceAll(
        STORE_NAME_TEMPLATE,
        templateVariables.clientName,
      );
    } else if (descriptionTemplate) {
      headerDescription =
        this.i18n?.translate(descriptionTemplate, templateVariables) || '';
    }

    if (typeof headerDescription === 'string') {
      this.headerDescription!.textContent = headerDescription;
    }
  }

  renderFooterContent() {
    const {
      legalTextVariant,
      legalTextTemplate,
      templateVariables,
      userNameKnown,
    } = this.state;
    const {clientName, privacyPolicyUrl, termsOfServiceUrl} = templateVariables;
    let content = '';

    switch (legalTextVariant) {
      case 'signup': {
        const shopTermsOfServiceLink = `<a href="https://shop.app/terms-of-service" target="_blank">${this.i18n?.translate(
          'legal.terms_of_service',
        )}</a>`;
        const shopTermsOfService = this.i18n?.translate(`legal.shop.email`, {
          clientName,
          termsOfService: shopTermsOfServiceLink,
        });

        const privacyPolicyLink =
          privacyPolicyUrl &&
          `<a href="${privacyPolicyUrl}" target="_blank">${this.i18n?.translate(
            'legal.privacy_policy',
          )}</a>`;
        const termsOfServiceLink =
          termsOfServiceUrl &&
          `<a href="${termsOfServiceUrl}" target="_blank">${this.i18n?.translate(
            'legal.terms',
          )}</a>`;
        const clientTermsOfService =
          (privacyPolicyLink &&
            termsOfServiceLink &&
            this.i18n?.translate(`legal.client`, {
              clientName,
              privacyPolicy: privacyPolicyLink,
              termsOfService: termsOfServiceLink,
            })) ||
          '';

        content = `${shopTermsOfService}&nbsp;${clientTermsOfService}`;
        break;
      }
      case 'generic':
        content =
          this.i18n?.translate(
            userNameKnown
              ? 'login_with_shop.auth_modal.login_footer_with_name'
              : legalTextTemplate,
            templateVariables,
          ) || '';
        break;
    }

    this.footerContent.innerHTML = content;
  }

  setHeaderVisible(isVisible: boolean) {
    this.modalHeader.classList.toggle(HIDDEN_ELEMENT_CLASS_NAME, !isVisible);

    if (!this.headerIframe) {
      this.headerIframe = this.rootElement.querySelector(
        `.${HEADER_CLASS_NAME}-container`,
      )! as HTMLIFrameElement;
      const headerContainerIframeBody =
        this.headerIframe.contentDocument?.querySelector('body')!;
      headerContainerIframeBody.innerHTML = HEADER_STYLE;
      this.headerContentsContainer = document.createElement('div');
      this.headerContentsContainer.classList.add(
        HEADER_CONTENTS_CONTAINER_CLASS_NAME,
      );

      const headerLogo = document.createElement('shop-swirl');
      this.headerTitle = document.createElement('h2');
      this.headerTitle.classList.add(HEADER_TITLE_CLASS_NAME);
      this.headerDescription = document.createElement('div');
      this.headerDescription.classList.add(HEADER_DESCRIPTION_CLASS_NAME);
      this.headerContentsContainer.appendChild(headerLogo);
      this.headerContentsContainer.appendChild(this.headerTitle);
      this.headerContentsContainer.appendChild(this.headerDescription);
      headerContainerIframeBody.appendChild(this.headerContentsContainer);

      if (window.ResizeObserver) {
        this.headerResizeObserver = new ResizeObserver(() => {
          this.refreshHeaderIframeHeight();
        });
        this.headerResizeObserver.observe(this.headerContentsContainer);
      } else {
        this.refreshHeaderIframeHeight();
      }
    }
  }

  /**
   * Resize iframe to match height of the modal header content
   */
  refreshHeaderIframeHeight() {
    const headerContainerIframeBody =
      this.headerIframe?.contentDocument?.querySelector('body')!;

    const heightStyle = `${
      this.headerContentsContainer?.getBoundingClientRect().height
    }px`;

    headerContainerIframeBody?.setAttribute('height', heightStyle);
    this.headerIframe?.setAttribute('height', heightStyle);
  }

  setHeaderDividerVisible(isVisible: boolean) {
    this.modalHeader.classList.toggle(HEADER_DIVIDER_CLASS_NAME, isVisible);
  }

  setFooterVisible(isVisible: boolean) {
    this.footerElement.classList.toggle(HIDDEN_ELEMENT_CLASS_NAME, !isVisible);
  }

  setCustomizedModalContent({
    modalTitle,
    modalDescription,
    modalLogo,
  }: ModalCustomizations) {
    if (modalTitle) {
      this._modalTitle = sanitizeModalCustomization(modalTitle);
    }
    if (modalDescription) {
      this._modalDescription = sanitizeModalCustomization(modalDescription);
    }
    if (modalLogo) {
      this._modalLogoSrc = sanitizeModalCustomization(modalLogo);
    }
  }

  getCustomizedModalContent() {
    return {
      modalTitle: this._modalTitle,
      modalDescription: this._modalDescription,
      modalLogo: this._modalLogoSrc,
    };
  }

  setTranslations(i18n: I18n) {
    this.i18n = i18n;
  }

  isModalCustomized() {
    return Boolean(
      this._modalTitle || this._modalDescription || this._modalLogoSrc,
    );
  }
}
